import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Globe, TrendingUp } from 'lucide-react';
import { MONTHLY_VOLUME_OPTIONS } from '../post-signup-onboarding-constants';

export interface ProcessingFormData {
	firstName: string;
	lastName: string;
	storeWebsite: string;
	monthlyVolume: string;
}

interface FormStepProps {
	formData: ProcessingFormData;
	onChange: (data: ProcessingFormData) => void;
	onNext: () => void;
	onPrev?: () => void;
}

/** Step 2: First name, last name, store/website, monthly volume. */
export function FormStep({ formData, onChange, onNext, onPrev }: FormStepProps) {
	const { t } = useTranslation('onboarding');
	return (
		<div className="space-y-8 text-left">
			<div className="space-y-2 text-center">
				<h2 className="text-foreground text-2xl font-bold tracking-tight">
					{t('form.title')}
				</h2>
				<p className="text-muted-foreground text-sm">
					{t('form.subtitle')}
				</p>
			</div>
			<div className="mx-auto grid max-w-md gap-4">
				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<Label htmlFor="onboarding-firstName" className="text-foreground text-sm font-medium">
							{t('form.firstName')}
						</Label>
						<Input
							id="onboarding-firstName"
							value={formData.firstName}
							onChange={(e) =>
								onChange({ ...formData, firstName: e.target.value })
							}
							placeholder={t('form.firstNamePlaceholder')}
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="onboarding-lastName" className="text-foreground text-sm font-medium">
							{t('form.lastName')}
						</Label>
						<Input
							id="onboarding-lastName"
							value={formData.lastName}
							onChange={(e) =>
								onChange({ ...formData, lastName: e.target.value })
							}
							placeholder={t('form.lastNamePlaceholder')}
						/>
					</div>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="onboarding-store" className="text-foreground text-sm font-medium">
						<Globe className="mb-0.5 mr-1 inline-block h-3.5 w-3.5" />
						{t('form.storeWebsite')}
					</Label>
					<Input
						id="onboarding-store"
						value={formData.storeWebsite}
						onChange={(e) =>
							onChange({ ...formData, storeWebsite: e.target.value })
						}
						placeholder={t('form.storeWebsitePlaceholder')}
					/>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="onboarding-volume" className="text-foreground text-sm font-medium">
						<TrendingUp className="mb-0.5 mr-1 inline-block h-3.5 w-3.5" />
						{t('form.monthlyVolume')}
					</Label>
					<Select
						value={formData.monthlyVolume}
						onValueChange={(v) =>
							onChange({ ...formData, monthlyVolume: v })
						}
					>
						<SelectTrigger id="onboarding-volume" className="w-full">
							<SelectValue placeholder={t('form.monthlyVolumePlaceholder')} />
						</SelectTrigger>
						<SelectContent>
							{MONTHLY_VOLUME_OPTIONS.map((opt) => (
								<SelectItem key={opt.value} value={opt.value}>
									{t(`volume.${opt.value}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="mt-2 flex gap-3">
					{onPrev && (
						<Button onClick={onPrev} variant="outline" size="lg" className="flex-1">
							{t('back')}
						</Button>
					)}
					<Button
						onClick={onNext}
						disabled={!formData.monthlyVolume}
						size="lg"
						className="flex-1"
					>
						{t('form.next')}
					</Button>
				</div>
			</div>
		</div>
	);
}
